import { useCallback, useEffect, useRef, useState } from "react";

import api from "../../../lib/api";

/*
 * Whether a runner shift covers now, and the hours collections run.
 *
 * The server refuses an off-shift pickup either way; this is what stops the
 * desk asking for one, and tells them when they can.
 */

function isCancel(err) {
  return err?.name === "CanceledError" || err?.code === "ERR_CANCELED";
}

/* A venue between shifts is the only thing that closes collections. A booking
 * the valet endpoint will not show, or a request that failed, must not leave
 * the desk unable to lodge a car. */
const OPEN = { open: true, windows: [] };

export default function usePickupCollections(bookingId) {
  const [collections, setCollections] = useState(OPEN);
  const [serverTime, setServerTime] = useState(null);
  /* The last instant this booking's card allows a pickup; null binds nothing. */
  const [expiresAt, setExpiresAt] = useState(null);
  const [loading, setLoading] = useState(Boolean(bookingId));

  const abortRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const load = useCallback(async () => {
    if (!bookingId) {
      setCollections(OPEN);
      setLoading(false);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);

    try {
      const view = await api.valetPickup(bookingId, {
        signal: controller.signal,
      });
      if (!mountedRef.current || controller.signal.aborted) return;
      setCollections(view?.collections ?? OPEN);
      setServerTime(view?.serverTime ?? null);
      setExpiresAt(view?.departure?.expiresAt ?? null);
    } catch (err) {
      if (!mountedRef.current || isCancel(err)) return;
      setCollections(OPEN);
    } finally {
      if (mountedRef.current && !controller.signal.aborted) setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    open: collections.open !== false,
    windows: collections.windows ?? [],
    serverTime,
    expiresAt,
    loading,
  };
}
